import { endpoints } from "@/configs/Settings";
import { HTTPResponseStatus } from "@/utils/enum";
import { useAuth } from "@/utils/useAuth";
import { useAxios } from "@/utils/useAxios";
import { PrescriptionTemplate } from "@/fluxvet/hooks/usePrescriptionTemplates";

/**
 * Converte um PrescriptionTemplate para QuestionnaireResponse FHIR R5
 *
 * @param template - Modelo de prescrição
 * @param organizationId - ID da organização
 * @param userId - ID do usuário criador
 * @returns QuestionnaireResponse FHIR R5
 */
export function convertTemplateToQuestionnaireResponse(
  template: Omit<PrescriptionTemplate, "id" | "createdAt" | "updatedAt">,
  organizationId: string,
  userId: string
): fhir5.QuestionnaireResponse {
  const items: fhir5.QuestionnaireResponseItem[] = [];

  // Informações do modelo
  items.push({
    linkId: "template-info",
    text: "Informações do Modelo",
    item: [
      {
        linkId: "template-name",
        text: "Nome do Modelo",
        answer: [{ valueString: template.name }],
      },
      ...(template.description
        ? [
            {
              linkId: "template-description",
              text: "Descrição do Modelo",
              answer: [{ valueString: template.description }],
            },
          ]
        : []),
    ],
  });

  // Tratamentos
  if (template.treatments && template.treatments.length > 0) {
    template.treatments.forEach((treatment) => {
      items.push({
        linkId: "treatments",
        text: "Tratamentos",
        item: [
          {
            linkId: "treatment-name",
            text: "Nome do Medicamento",
            answer: [{ valueString: treatment.name }],
          },
          {
            linkId: "treatment-dosage",
            text: "Posologia",
            answer: [{ valueString: treatment.dosage }],
          },
          ...(treatment.description
            ? [
                {
                  linkId: "treatment-description",
                  text: "Descrição de Uso",
                  answer: [{ valueString: treatment.description }],
                },
              ]
            : []),
        ],
      });
    });
  }

  // Exames/Procedimentos
  if (template.examsOrProcedures && template.examsOrProcedures.length > 0) {
    template.examsOrProcedures.forEach((exam) => {
      items.push({
        linkId: "exams",
        text: "Exames e Procedimentos",
        item: [
          {
            linkId: "exam-description",
            text: "Descrição do Exame ou Procedimento",
            answer: [{ valueString: exam.description }],
          },
          ...(exam.notes
            ? [
                {
                  linkId: "exam-notes",
                  text: "Observações",
                  answer: [{ valueString: exam.notes }],
                },
              ]
            : []),
        ],
      });
    });
  }

  // Relatórios
  if (template.reports && template.reports.length > 0) {
    template.reports.forEach((report) => {
      items.push({
        linkId: "reports",
        text: "Relatórios",
        item: [
          {
            linkId: "report-content",
            text: "Conteúdo do Relatório",
            answer: [{ valueString: report.content }],
          },
        ],
      });
    });
  }

  // Orientações
  if (template.guidelines) {
    items.push({
      linkId: "guidelines",
      text: "Orientações Gerais",
      answer: [{ valueString: template.guidelines }],
    });
  }

  // Questionários clínicos (pós-consulta etc.) — mesmo modelo conceitual do fluxo humano.
  if (template.questionnaires && template.questionnaires.length > 0) {
    template.questionnaires.forEach((q) => {
      items.push({
        linkId: "template-questionnaire",
        text: "Questionário do protocolo",
        item: [
          {
            linkId: "tq-questionnaire-id",
            text: "ID do Questionnaire",
            answer: [{ valueString: q.questionnaireId }],
          },
          {
            linkId: "tq-title",
            text: "Título",
            answer: [{ valueString: q.title }],
          },
          {
            linkId: "tq-name",
            text: "Nome",
            answer: [{ valueString: q.name }],
          },
          {
            linkId: "tq-label",
            text: "Label",
            answer: [{ valueString: q.label }],
          },
          ...(q.usageType
            ? [
                {
                  linkId: "tq-usage-type",
                  text: "Tipo de uso",
                  answer: [{ valueString: q.usageType }],
                },
              ]
            : []),
        ],
      });
    });
  }

  const now = new Date().toISOString();

  return {
    resourceType: "QuestionnaireResponse",
    questionnaire: `Questionnaire/${endpoints.PROTOCOL_ID}`,
    status: "completed",
    authored: now,
    author: {
      reference: `Organization/${organizationId}`,
    },
    source: {
      reference: `Practitioner/${userId}`,
    },
    item: items,
  };
}

/**
 * Cria um novo modelo de prescrição no servidor FHIR
 *
 * @param template - Dados do modelo (sem id, createdAt, updatedAt)
 * @param organizationId - ID da organização
 * @param userId - ID do usuário criador
 * @returns QuestionnaireResponse criado
 */
export async function PostPrescriptionTemplate(
  template: Omit<PrescriptionTemplate, "id" | "createdAt" | "updatedAt">,
  organizationId: string,
  userId: string
) {
  const questionnaireResponse = convertTemplateToQuestionnaireResponse(
    template,
    organizationId,
    userId
  );

  const bearerToken = await useAuth.getUserToken();
  const headers = {
    Accept: "*/*",
    Authorization: bearerToken,
    "Content-Type": "application/fhir+json",
  };

  const response = await useAxios
    .post(`${endpoints.ADMFHIR}/QuestionnaireResponse`, questionnaireResponse, {
      headers,
      timeout: 20000,
    })
    .then((response) => {
      if (response.status !== HTTPResponseStatus.Created) {
        throw new Error(`PostPrescriptionTemplate: ${response.status}`);
      }
      return response.data;
    })
    .catch((error) => {
      console.error("PostPrescriptionTemplate Error:", error);
      throw new Error(
        `Erro ao salvar modelo: ${error?.response?.status || "Desconhecido"}`
      );
    });

  return response;
}
